// Configuration de l'API
// En production, utiliser des URLs relatives (backend et frontend sur le même serveur)
// En développement, utiliser localhost
const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction 
  ? '/api'  // URL relative en production
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');
const BACKEND_BASE_URL = isProduction 
  ? ''  // URL relative en production
  : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000');

// Classe pour gérer les appels API
class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.backendURL = BACKEND_BASE_URL;
  }

  // Méthode pour ajouter les credentials à l'URL
  addCredentialsToUrl(url, credentials) {
    const params = new URLSearchParams(url.includes('?') ? url.split('?')[1] : '');
    params.append('username', credentials.username);
    params.append('password', credentials.password);
    const baseUrl = url.split('?')[0];
    return `${baseUrl}?${params.toString()}`;
  }

  // Méthode pour faire des requêtes avec gestion d'erreurs
  async request(endpoint, options = {}) {
    try {
      const url = `${this.baseURL}${endpoint}`;
      
      const config = {
        ...options,
        headers: {
          ...options.headers,
        }
      };

      // Si l'utilisateur est connecté et que c'est une requête qui nécessite une authentification
      const isLoggedIn = localStorage.getItem('isAdminLoggedIn') === 'true';
      const credentials = localStorage.getItem('adminCredentials');
      const needsAuth = options.method === 'POST' || options.method === 'PUT' || options.method === 'DELETE' || endpoint.includes('/admin/');
      
      if (needsAuth && isLoggedIn && credentials) {
        const credentialsObj = JSON.parse(credentials);

        // Pour les requêtes GET et DELETE, ajouter les credentials comme paramètres d'URL
        if (!options.method || options.method === 'GET' || options.method === 'DELETE') {
          config.url = this.addCredentialsToUrl(url, credentialsObj);
        }
        // Pour FormData, ajouter les credentials comme champs du formulaire
        else if (options.body instanceof FormData) {
          // Créer un nouveau FormData sans les credentials (au cas où ils seraient déjà présents)
          const formData = new FormData();
          for (let [key, value] of options.body.entries()) {
            if (key !== 'username' && key !== 'password') {
              formData.append(key, value);
            }
          }
          // Ajouter les credentials une seule fois
          formData.append('username', credentialsObj.username);
          formData.append('password', credentialsObj.password);
          config.body = formData;
        }
        // Pour les requêtes JSON
        else {
          config.headers['Content-Type'] = 'application/json';
          const bodyData = typeof options.body === 'string' ? JSON.parse(options.body) : options.body || {};
          // Supprimer les credentials s'ils sont déjà présents
          delete bodyData.username;
          delete bodyData.password;
          config.body = JSON.stringify({
            ...bodyData,
            username: credentialsObj.username,
            password: credentialsObj.password
          });
        }
      } else if (options.body) {
        if (options.body instanceof FormData) {
          config.body = options.body;
        } else {
          config.headers['Content-Type'] = 'application/json';
          config.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
        }
      }

      const response = await fetch(config.url || url, config);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur API:', error);
      throw error;
    }
  }

  // Méthode utilitaire pour construire l'URL complète d'une image
  getImageUrl(imagePath) {
    if (!imagePath) return '/img/defaultImgCar.webp';
    if (imagePath.startsWith('http')) return imagePath;
    
    if (imagePath.startsWith('/uploads')) {
      return `${this.backendURL}${imagePath}`;
    } else {
      return imagePath;
    }
  }

  // Méthode utilitaire pour traiter les données de voiture
  processCarData(car) {
    if (!car) return car;
    
    return {
      ...car,
      images: car.images?.map(img => this.getImageUrl(img)) || ['/img/defaultImgCar.webp']
    };
  }

  // Méthodes pour les voitures
  async getCars(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/cars${queryParams ? `?${queryParams}` : ''}`;
    const response = await this.request(endpoint);
    
    if (response.success && response.data) {
      response.data = response.data.map(car => this.processCarData(car));
    }
    
    return response;
  }

  async getCarById(id) {
    const response = await this.request(`/cars/${id}`);
    
    if (response.success && response.data) {
      response.data = this.processCarData(response.data);
    }
    
    return response;
  }

  async createCar(carData) {
    const response = await this.request('/cars', {
      method: 'POST',
      body: carData
    });
    
    if (response.success && response.data) {
      response.data = this.processCarData(response.data);
    }
    
    return response;
  }

  async updateCar(id, carData) {
    const response = await this.request(`/cars/${id}`, {
      method: 'PUT',
      body: carData
    });
    
    if (response.success && response.data) {
      response.data = this.processCarData(response.data);
    }
    
    return response;
  }

  async deleteCar(id) {
    return this.request(`/cars/${id}`, {
      method: 'DELETE'
    });
  }

  async getCarStats() {
    return this.request('/cars/admin/stats');
  }

  async getCarsFeatured() {
    const response = await this.request('/cars/featured');
    
    if (response.success && response.data) {
      response.data = response.data.map(car => this.processCarData(car));
    }
    
    return response;
  }

  // Méthodes pour l'authentification
  async login(credentials) {
    try {
      const response = await this.request('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });
      
      if (response.success) {
        localStorage.setItem('isAdminLoggedIn', 'true');
        localStorage.setItem('adminCredentials', JSON.stringify(credentials));
      }
      
      return response;
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      throw error;
    }
  }

  async logout() {
    try {
      localStorage.removeItem('isAdminLoggedIn');
      localStorage.removeItem('adminCredentials');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  }

  // Méthode utilitaire pour vérifier si l'utilisateur est connecté
  isAuthenticated() {
    return localStorage.getItem('isAdminLoggedIn') === 'true';
  }
}

// Exporter une instance unique (singleton)
const apiService = new ApiService();
export default apiService;